import { ReactComponent as CaretDownGlyph } from '@/assets/images/svg/glyphs/caret-down.svg';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import { kebabCase } from 'lodash';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DropDownOption from '../_atoms/DropDownOption';

export type FilterSelector_Category = {
  title: string;
  filters: string[];
  selections: string[];
};

interface SelectorProps extends PropsWithClassName {
  categories: FilterSelector_Category[];
  onFilterToggle: (category: FilterSelector_Category, filter: string, selected: boolean) => void;
}

const DropDownSelectors: FC<SelectorProps> = (props) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!expandedCategory || !ref.current) return;

    const onClick: EventListenerOrEventListenerObject = (e) => {
      if (!ref.current!.contains(e.target as HTMLElement)) {
        setExpandedCategory(null);
      }
    };

    window.addEventListener('click', onClick);

    return () => window.removeEventListener('click', onClick);
  }, [expandedCategory, ref.current]);

  return (
    <FiltersGrid ref={ref}>
      {props.categories?.map((category, i) => {
        const categoryOpen = expandedCategory === category.title;
        const categoryCount = category.selections.length;

        return (
          <FilterWrapper key={kebabCase(category.title.toLowerCase())}>
            <FilterButton
              aria-expanded={categoryOpen}
              onClick={() => {
                setExpandedCategory(categoryOpen ? null : category.title);
              }}
            >
              <FilterType>
                {category.title}
                {categoryCount > 0 ? ` (${categoryCount})` : null}
              </FilterType>

              <FilterCarat>
                <CaretDownGlyph width={16} />
              </FilterCarat>
            </FilterButton>

            {category.filters && categoryOpen && (
              <DropDownOptions>
                {category.filters.map((filter, i) => {
                  const checked = category.selections?.includes(filter);

                  return (
                    <DropDownOption
                      key={filter}
                      title={filter}
                      checked={checked}
                      onToggle={(state) => props.onFilterToggle(category, filter, state)}
                    >
                      {filter}
                    </DropDownOption>
                  );
                })}
              </DropDownOptions>
            )}
          </FilterWrapper>
        );
      })}
    </FiltersGrid>
  );
};

// #region - Components

const FiltersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${remCalc(20)};

  ${mediaBreakpointUp('tablet')} {
    display: grid;
    grid-template-columns: repeat(6, 1fr);

    > div:first-child {
      grid-column: 1 / span 3;
    }

    > div:nth-child(2) {
      grid-column: 4 / span 3;
    }
  }
`;

const DropDownOptions = styled.ul`
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  position: absolute;
  top: ${remCalc(40)};
  left: 0;
  ${paddingX(8)};
  ${paddingY(16)};
  width: 100%;
  z-index: 2;

  border-radius: 4px;
  box-shadow: 0px 0px 10px 0px ${hexToRGBA(COLORS.BLACK, 0.1)};
`;

const FilterWrapper = styled.div`
  position: relative;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${paddingX(16)};
  ${paddingY(8)};
  position: relative;
  width: 100%;

  border: 1px solid ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(50)};
  width: 100%;

  &:hover,
  &:active,
  &:focus {
    outline: none;
  }
`;

const FilterType = styled.div`
  align-items: center;
`;

const FilterCarat = styled.div`
  align-items: center;

  svg {
    path {
      fill: ${hexToRGBA(COLORS.BLACK, 0.4)};
    }
  }
`;

// #endregion - Components

export default DropDownSelectors;
